import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/community", { replace: true });
  }, [user, navigate]);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/community";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back, Forger." : "Account forged.");
      navigate("/community");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full bg-white/[0.04] border border-white/12 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-ember transition-colors";

  return (
    <main data-testid="login-page" className="min-h-screen pt-32 pb-24">
      <div className="mx-auto w-[min(440px,calc(100%-32px))]">
        <div className="text-center mb-10">
          <div className="eyebrow-label mb-4">Join the Forge</div>
          <h1 className="headline text-4xl sm:text-5xl">
            <span className="text-white">Sign </span>
            <span className="text-forge">in</span>
          </h1>
        </div>

        <button
          data-testid="google-signin-btn"
          onClick={googleSignIn}
          className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl bg-white text-black font-semibold text-sm hover:bg-white/90 transition-colors mb-6"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5">
            <path fill="#4285F4" d="M23 12.2c0-.8-.1-1.6-.2-2.3H12v4.4h6.2c-.3 1.4-1.1 2.6-2.3 3.4v2.8h3.7c2.2-2 3.4-5 3.4-8.3z" />
            <path fill="#34A853" d="M12 24c3.1 0 5.7-1 7.6-2.8l-3.7-2.8c-1 .7-2.3 1.1-3.9 1.1-3 0-5.6-2-6.5-4.8H1.7v3C3.6 21.3 7.5 24 12 24z" />
            <path fill="#FBBC05" d="M5.5 14.7c-.2-.7-.4-1.4-.4-2.2s.1-1.5.4-2.2v-3H1.7C.9 8.9.5 10.4.5 12s.4 3.1 1.2 4.5l3.8-1.8z" />
            <path fill="#EA4335" d="M12 4.8c1.7 0 3.2.6 4.4 1.7l3.3-3.3C17.7 1.2 15.1 0 12 0 7.5 0 3.6 2.7 1.7 6.6l3.8 3c.9-2.8 3.5-4.8 6.5-4.8z" />
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6">
          <span className="flex-1 h-px bg-white/10" />
          <span className="display text-[10px] tracking-[0.3em] text-white/25">or email</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>

        <div className="flex gap-2 mb-6">
          {[
            { id: "login", label: "Sign in" },
            { id: "register", label: "Create account" },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`auth-tab-${tab.id}`}
              onClick={() => {
                setMode(tab.id);
                setError("");
              }}
              className={`flex-1 display text-[11px] tracking-[0.2em] py-3 rounded-xl border transition-colors ${
                mode === tab.id
                  ? "border-forge text-forge bg-forge/10"
                  : "border-white/10 text-white/45 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <input
              data-testid="auth-name-input"
              className={field}
              placeholder="Display name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          )}
          <input
            data-testid="auth-email-input"
            className={field}
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
          <input
            data-testid="auth-password-input"
            className={field}
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
          {error && (
            <p data-testid="auth-error" className="text-sm text-forge">
              {error}
            </p>
          )}
          <button
            data-testid="auth-submit-btn"
            disabled={busy}
            className="w-full display text-sm tracking-[0.2em] py-4 rounded-xl bg-forge text-white hover:bg-ember disabled:opacity-50 transition-colors"
          >
            {busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="text-xs text-white/30 text-center mt-8 leading-relaxed">
          Anyone can read the community. An account is needed to post, comment and vote.
        </p>
      </div>
    </main>
  );
}
